import type { TradeLogicJsonObject, TradeLogicJsonValue } from '../model';

export type QuantityExpressionMode = 'blank' | 'literal' | 'json';

export interface QuantityExpressionInputState {
    mode: QuantityExpressionMode;
    literalValue: string;
    jsonValue: string;
}

export const createLiteralQuantityExpression = (value: number): TradeLogicJsonObject => ({
    kind: 'literal',
    value,
    valueType: 'number'
});

export const buildQuantityExpressionInputState = (value: TradeLogicJsonValue | undefined): QuantityExpressionInputState => {
    if (isNumberLiteralQuantityExpression(value)) {
        return {
            jsonValue: JSON.stringify(value, null, 2),
            literalValue: String(value.value),
            mode: 'literal'
        };
    }

    if (value === undefined) {
        return {
            jsonValue: '',
            literalValue: '1',
            mode: 'blank'
        };
    }

    return {
        jsonValue: JSON.stringify(value, null, 2),
        literalValue: '1',
        mode: 'json'
    };
};

export const parseQuantityExpressionInputState = (state: QuantityExpressionInputState): TradeLogicJsonValue | undefined => {
    if (state.mode === 'blank') return undefined;

    if (state.mode === 'json') return parseOptionalJsonValue(state.jsonValue, 'Size expression');

    const quantity = parseOptionalNumber(state.literalValue, 'Size fraction');

    if (quantity === null) {
        throw new Error('Size fraction is required in numeric literal mode.');
    }

    if (quantity <= 0) {
        throw new Error('Size fraction must be greater than 0.');
    }

    return createLiteralQuantityExpression(quantity);
};

const isNumberLiteralQuantityExpression = (value: TradeLogicJsonValue | undefined): value is TradeLogicJsonObject & { value: number } => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

    return value.kind === 'literal' && value.valueType === 'number' && typeof value.value === 'number' && Number.isFinite(value.value);
};

const parseOptionalJsonValue = (rawValue: string, label: string): TradeLogicJsonValue | undefined => {
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') return undefined;

    try {
        return JSON.parse(trimmedValue) as TradeLogicJsonValue;
    } catch {
        throw new Error(`${label} must be valid JSON or blank.`);
    }
};

const parseOptionalNumber = (rawValue: string, label: string): number | null => {
    const trimmedValue = rawValue.trim();

    if (trimmedValue === '') return null;

    const parsed = Number(trimmedValue);

    if (!Number.isFinite(parsed)) {
        throw new Error(`${label} must be numeric`);
    }

    return parsed;
};
